import * as yup from 'yup';
import { ObjectSchema } from 'yup'; // Import the type for strong typing
import { SchoolStructureData, Grade, Section } from '../types'; // Assuming Section is also exported

const gradeSchema = yup.object().shape({
  name: yup
    .string()
    .matches(/^[A-Za-z0-9\-. ]+$/, {
      message: 'Only letters, numbers, dots, hyphens, and spaces are allowed.',
      excludeEmptyString: true
    })
    .test(
      'unique-grade-name-across-sections',
      'Grade name already exists',
      function (value) {
        if (!value) {
          return true;
        }

        const rootData = this.from?.find((context) => context.value?.sections)
          ?.value as SchoolStructureData | undefined;

        if (!rootData || !rootData.sections) {
          return true;
        }

        const normalize = (str: string) =>
          str.replace(/\s+/g, ' ').trim().toLowerCase();

        const normalizedValue = normalize(value);

        let count = 0;

        for (const sectionKey in rootData.sections) {
          const section = rootData.sections[sectionKey];
          if (section.grades) {
            for (let i = 0; i < section.grades.length; i++) {
              const grade = section.grades[i];

              if (grade.name && normalize(grade.name) === normalizedValue) {
                count++;
              }
            }
          }
        }

        return count <= 1;
      }
    )
    .required('Grade Name is required'),

  displayName: yup
    .string()
    .required('Display Name is required')
    .matches(/^[A-Za-z0-9\-. ]+$/, {
      message: 'Only letters, numbers, dots, hyphens, and spaces are allowed.',
      excludeEmptyString: true
    }),
  divisions: yup
    .array()
    .of(
      yup
        .string()
        .matches(/^([A-Za-z0-9\-()]+)(,[A-Za-z0-9\-()]+)*$/, {
          message: 'Only letters, numbers, (), hyphen allowed.',
          excludeEmptyString: true
        })
        .required('Division name cannot be empty')
    )
    .min(1, 'At least one division is required for a grade.')
    .required('Divisions are required'),
  board: yup.string()
});

const sectionSchema = yup.object().shape({
  grades: yup
    .array()
    .of(gradeSchema)
    .min(1, 'At least one grade is required for a section.')
    .required()
});

// Apply the explicit type to your schema variable
export const schoolStructureFormSchema: ObjectSchema<SchoolStructureData> = yup
  .object()
  .shape({
    school: yup.string().required('School is required'),
    houses: yup
      .array()
      .of(
        yup
          .string()
          .matches(
            /^[A-Za-z0-9 ]+$/,
            'Only letters, numbers, and spaces are allowed.'
          )
      ),

    // --- CORRECTED SECTIONS SCHEMA ---
    sections: yup.lazy((obj: Record<string, any>) => {
      const dynamicSectionSchemas: Record<
        string,
        ObjectSchema<{ grades: Grade[] }>
      > = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          dynamicSectionSchemas[key] = sectionSchema;
        }
      }
      return yup
        .object()
        .shape(dynamicSectionSchemas)

        .test(
          'section-keys-validation',
          'One or more section names are invalid.',
          (value: Section | undefined) => {
            // Explicitly type value as Section
            if (!value) return true;
            const sectionKeys = Object.keys(value);

            if (sectionKeys.length === 0) {
              return false;
            }

            for (const sectionKey of sectionKeys) {
              if (!/^[A-Za-z0-9-]+$/.test(sectionKey)) {
                return false;
              }
            }
            return true;
          }
        );
    }),

    newSectionName: yup
      .string()
      .nullable()
      .notRequired()
      .matches(/^[A-Za-z0-9-]+$/, {
        message:
          'Only letters, numbers, and hyphens are allowed for section names.',
        excludeEmptyString: true
      })
      .test(
        'unique-section-name',
        'Section name already exists',
        function (value) {
          if (!value) return true;
          const sections = this.parent.sections || {};
          return !Object.keys(sections).includes(value);
        }
      )
  });
